<script setup lang="ts">
/**
 * Series legend of the kit's charts: a key mark (square for bars, stroke for
 * lines) and the series name per entry, wrapping in rows. Past `max` entries
 * the rest collapse into „+n“.
 *
 * Hovering or focusing an entry reports its key through `v-model:highlighted`,
 * so the chart can bring that series forward and fade the others. The legend
 * is the identity channel the chart's colours lean on — the names never wear
 * the series colour, only the key mark does.
 */
import { computed } from 'vue';
import type { NxChartKey } from './series';

export interface NxChartLegendItem {
  key: NxChartKey;
  label: string;
  color: string;
}

const props = withDefaults(
  defineProps<{
    items: readonly NxChartLegendItem[];
    /** Key mark: `rect` for bars and areas, `line` for lines. */
    shape?: 'rect' | 'line';
    /** Entries shown before „+n“. */
    max?: number;
  }>(),
  { shape: 'rect', max: 8 }
);

const highlighted = defineModel<NxChartKey | null>('highlighted', { default: null });

const shown = computed(() => props.items.slice(0, props.max));
const overflow = computed(() => Math.max(0, props.items.length - props.max));
const overflowTitle = computed(() =>
  props.items
    .slice(props.max)
    .map((i) => i.label)
    .join(', ')
);
</script>

<template>
  <ul v-if="props.items.length" class="nx-chart-legend" role="list" data-testid="nx-chart-legend">
    <li
      v-for="item in shown"
      :key="item.key"
      class="nx-chart-legend__item"
      :class="{ 'is-dimmed': highlighted !== null && highlighted !== item.key }"
      @mouseenter="highlighted = item.key"
      @mouseleave="highlighted = null"
    >
      <span
        class="nx-chart-legend__key"
        :class="`is-${props.shape}`"
        :style="{ background: item.color }"
        aria-hidden="true"
      />
      <span class="nx-chart-legend__label" :title="item.label">{{ item.label }}</span>
    </li>
    <li v-if="overflow > 0" class="nx-chart-legend__overflow" :title="overflowTitle">
      +{{ overflow }}
    </li>
  </ul>
</template>

<style scoped>
.nx-chart-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-2xs) var(--nxt-space-md);
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: var(--nxt-font-size-xs);
  line-height: var(--nxt-line-height-tight);
}
.nx-chart-legend__item {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-width: 0;
  max-width: calc(var(--nxt-size-unit) * 10);
  transition: opacity var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-chart-legend__item.is-dimmed {
  opacity: 0.4;
}
.nx-chart-legend__key {
  flex: 0 0 auto;
  width: var(--nxt-dot-size-md);
  height: var(--nxt-dot-size-md);
  border-radius: calc(var(--nxt-hairline) * 2);
}
.nx-chart-legend__key.is-line {
  width: calc(var(--nxt-dot-size-md) * 1.75);
  height: calc(var(--nxt-hairline) * 2.5);
  border-radius: var(--nxt-radius-full);
}
.nx-chart-legend__label {
  overflow: hidden;
  color: var(--nxt-fg-subtle);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-chart-legend__overflow {
  color: var(--nxt-fg-muted);
  font-weight: var(--nxt-font-weight-semibold);
}
@media (prefers-reduced-motion: reduce) {
  .nx-chart-legend__item {
    transition: none;
  }
}
</style>
